class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.cart_icon = document.querySelector('#cart-icon-bubble-short')?'cart-icon-bubble-short':'cart-icon-bubble';
    this.cart_icon_mobile = document.querySelector('#cart-icon-bubble--mobile-short')?'cart-icon-bubble--mobile-short':'cart-icon-bubble--mobile';
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
    this.opened = false;
  }
  setHeaderCartIconAccessibility() {
    // The header cart icon (top of the page, both the icon+text and icon-only
    // variants, desktop and mobile) should navigate straight to the cart page
    // when clicked, not open the drawer. Every other cart-icon-bubble instance
    // (e.g. the footer sticky nav) keeps the original drawer-opening behaviour.
    // This only changes what happens on a direct click of these links — the
    // drawer still opens automatically after adding an item to cart, since
    // that flow calls this.open() directly and doesn't go through here.
    var headerCartIconIds = ['cart-icon-bubble', 'cart-icon-bubble-short', 'cart-icon-bubble--mobile', 'cart-icon-bubble--mobile-short'];
    document.querySelectorAll(`[id*="cart-icon-bubble"]`).forEach((cartLink) => {
      if (headerCartIconIds.indexOf(cartLink.id) !== -1) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    })
  }

  open(triggeredBy) {
    this.opened = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('active');
      this.classList.add('animate');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if(document.querySelectorAll('.popup-modal.active').length == 0){
      // iOS Safari doesn't fully honour overflow:hidden on <body> for touch
      // scrolling, so the page behind the drawer can still be scrolled while
      // it's open — which can leave a gap of real page content visible past
      // the bottom of the drawer panel. Recording the scroll offset and
      // pinning the body in place (see body.iov-modal-scroll-lock in
      // component-modal.css) is the standard fix for this; the offset is
      // restored on close below.
      window.iovScrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = -window.iovScrollLockY + 'px';
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('iov-modal-scroll-lock');
    }

  }
  close() {
    this.classList.remove('animate');
    setTimeout(() => {
      this.classList.remove('active');
      if(document.querySelectorAll('.popup-modal.active').length == 0){
        document.body.classList.remove('overflow-hidden');
        document.body.classList.remove('iov-modal-scroll-lock');
        document.body.style.paddingRight = '';
        document.body.style.top = '';
        window.scrollTo({ top: window.iovScrollLockY || 0, left: 0, behavior: 'instant' });
      }
    }, 250);
    removeTrapFocus(this.activeElement);
    this.opened = false;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Parse the single cart-drawer section once, replace its contents, then
  // derive every smaller UI update (cart counts and free-delivery progress)
  // from that same response. This avoids asking Shopify to render three extra
  // sections after every add-to-cart action.
  updateSections(parsedState) {
    const sectionHTML = parsedState?.sections?.['cart-drawer'];
    if (!sectionHTML) return;

    const documentFragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newDrawerRoot = documentFragment.querySelector('cart-drawer');
    const newDrawer = documentFragment.querySelector('#CartDrawer');
    const currentDrawer = this.querySelector('#CartDrawer');

    if (newDrawer && currentDrawer) {
      currentDrawer.innerHTML = newDrawer.innerHTML;
    }

    const itemCount = Number(newDrawerRoot?.dataset.cartItemCount);
    if (Number.isFinite(itemCount)) this.updateCartCounts(itemCount);

    const newFreeDelivery = documentFragment.querySelector('#cart-page-free-delivery');
    if (newFreeDelivery) {
      ['#product-page-free-delivery', '#cart-page-free-delivery'].forEach((selector) => {
        const target = document.querySelector(selector);
        if (target && target !== this.querySelector(selector)) {
          target.innerHTML = newFreeDelivery.innerHTML;
        }
      });
    }

    this.classList.toggle('is-empty', itemCount === 0);
    this.productId = parsedState.id;
  }

  updateCartCounts(itemCount) {
    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      // Some Wokiee header variants use the bubble itself as the number; others
      // use a wrapper containing a visible count and screen-reader label.
      if (bubble.matches('span.header__button__bubble__count') || bubble.children.length === 0) {
        bubble.textContent = itemCount;
        return;
      }

      let visibleCount = bubble.querySelector('.header__button__bubble__count');
      if (!visibleCount && itemCount > 0) {
        visibleCount = document.createElement('span');
        visibleCount.className = 'body3-no-scale header__button__bubble__count';
        visibleCount.setAttribute('aria-hidden', 'true');
        bubble.prepend(visibleCount);
      }
      if (visibleCount) visibleCount.textContent = itemCount > 0 ? itemCount : '';
      bubble.hidden = itemCount === 0;
    });
  }

  renderContents(parsedState) {
    this.updateSections(parsedState);
    requestAnimationFrame(() => this.open());
  }

  renderContentsWithoutOpening(parsedState) {
    this.updateSections(parsedState);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML || '';
  }

  // Only the complete drawer is requested. Its returned markup includes the
  // authoritative item count and free-delivery bar used to update the rest of
  // the page locally.
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  // Legacy Wokiee cart.js still calls these methods after quantity changes.
  // Recommendations are intentionally disabled, so keep harmless no-ops to
  // prevent a JavaScript exception from interrupting the cart refresh.
  clearAdditionalProducts() {}
  setUpdRecom() {}
  updateRecommendations() {}

  setActiveElement(element) {
    this.activeElement = element;
  }
  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    this.cart_icon = document.querySelector('#cart-icon-bubble-short')?'cart-icon-bubble-short':'cart-icon-bubble';
    this.cart_icon_mobile = document.querySelector('#cart-icon-bubble--mobile-short')?'cart-icon-bubble--mobile-short':'cart-icon-bubble--mobile';
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: this.cart_icon,
        section: this.cart_icon,
        selector: '.shopify-section',
      },
      {
        id: this.cart_icon_mobile,
        section: this.cart_icon_mobile,
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);